import { IIConType } from '../../../_metronic/helpers'
import { TIConName } from '../../../_metronic/helpers/icons-config/icons'
import { IAppNotificationPayload } from '../../app.interfaces'
import AlertBoxLayout from '../AlertBoxLayout/AlertBoxLayout'
import { resolveItemTitle } from './notificationTitle'
import styles from './NotificationModal.module.scss'

interface INotificationVariant {
    /** Colour of the header icon. */
    accent: string;
    /** Bootstrap variant driving the footer button. */
    button: string;
    iconName: TIConName;
    iconType: IIConType;
}

const VARIANTS: { [x: string]: INotificationVariant } = {
    success: { accent: '#17c653', button: 'success', iconName: 'check-circle', iconType: 'solid' },
    info: { accent: '#7239ea', button: 'info', iconName: 'information-5', iconType: 'solid' },
    danger: { accent: '#f8285a', button: 'danger', iconName: 'information', iconType: 'solid' },
    error: { accent: '#f8285a', button: 'danger', iconName: 'information', iconType: 'solid' },
    white: { accent: '#5e6278', button: 'primary', iconName: 'information-3', iconType: 'solid' },
}

/** Header text for a clubbed group whose entries do not share one title. */
const GROUP_TITLES: { [x: string]: string } = {
    success: 'Success',
    info: 'Information',
    danger: 'Error',
    error: 'Error',
    white: 'General info',
}

const FALLBACK_MESSAGE = 'No information'

interface NotificationModalProps {
    open: boolean;
    /** Notifications awaiting acknowledgement. More than one renders as a single clubbed dialog. */
    notifications: IAppNotificationPayload[];
    onClose: () => void;
}

const NotificationModal = ({ open, notifications, onClose }: NotificationModalProps) => {
    const [first] = notifications
    // `type` also arrives from SignalR payloads, so fall back instead of trusting the union.
    const type = first?.type || 'danger'
    const variant = VARIANTS[type] || VARIANTS.white
    const isClubbed = notifications.length > 1
    // A clubbed dialog is headed by the severity, because each card names its own area.
    const groupTitle = GROUP_TITLES[type] || GROUP_TITLES.white
    const title = (!isClubbed && first?.title) || groupTitle

    const renderMessages = () => {
        if (!isClubbed) return <span className={styles.message}>{first?.msg || FALLBACK_MESSAGE}</span>

        return (
            <ul className={styles.list}>
                {notifications.map((notification, index) => (
                    <li className={styles.listItem} key={`${notification.title}-${notification.msg}-${index}`}>
                        <span className={styles.listItemTitle}>{resolveItemTitle(notification)}</span>
                        <span className={styles.message}>{notification.msg || FALLBACK_MESSAGE}</span>
                    </li>
                ))}
            </ul>
        )
    }

    return (
        <AlertBoxLayout
            open={open}
            onClose={onClose}
            title={title}
            msg={first?.msg || FALLBACK_MESSAGE}
            type={variant.button}
            icon={{ color: variant.accent, name: variant.iconName, type: variant.iconType }}
            renderMsg={renderMessages}
            // Notifications can be raised while another modal is open, so they sit above it.
            style={{ zIndex: 1065 }}
            backdropClassName={styles.backdrop}
            contentClassName={styles.content}
            bodyClassName='p-4 pt-2'
        />
    )
}

export default NotificationModal
